import {Routes} from '@angular/router';
import {ERROR_PATH} from '@npck/libs/commons';
import {AppVersionComponent, VERSION_PATH} from '@npck/libs/ui-kit';
import {USER_ID} from './domain/query-params.keys';
import {
    adminPanelGuard,
    adminPanelUsersGuard,
    afCentreGuard,
    analyticsGuard,
    authGuard,
    npckAdminGuard,
    refundsGuard,
} from './infra/auth-guard.service';
import {MainLayoutComponent} from './layout/main-layout/main-layout.component';
import {AdminComponent} from './pages/admin/admin.component';
import {DictionaryCreateEditComponent} from './pages/admin/dictionary/dictionary-create-edit/dictionary-create-edit.component';
import {OrganizationCreateEditComponent} from './pages/admin/organizations/create-edit/organization-create-edit.component';
import {OrganizationDetailsComponent} from './pages/admin/organizations/details/organization-details.component';
import {OrganizationDocumentCreateEditComponent} from './pages/admin/organizations/documents/organization-document-create-edit.component';
import {UserCreateEditComponent} from './pages/admin/users/create-edit/user-create-edit.component';
import {UserDetailsComponent} from './pages/admin/users/details/user-details.component';
import {AfCentreComponent} from './pages/af-centre/af-centre.component';
import {AnalyticsComponent} from './pages/analytics/analytics.component';
import {
    ClassificationCreateEditCommonInformationComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-create-edit-common-information/classification-create-edit-common-information.component';
import {
    ClassificationDetailsComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-details.component';
import {
    ClassificationFraudDashboardsComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-fraud-dashboards.component';
import {FraudReportDetailsComponent} from './pages/analytics/fraud-reports/details/fraud-report-details.component';
import {FraudReportsComponent} from './pages/analytics/fraud-reports/fraud-reports.component';
import {DashboardFormComponent} from './pages/analytics/incident-fraud-dashboards/dashboard-form/dashboard-form.component';
import {IncidentsFraudDashboardsComponent} from './pages/analytics/incident-fraud-dashboards/incidents-fraud-dashboards.component';
import {CreateEditTrendComponent} from './pages/analytics/trends/create-edit/create-edit-trend.component';
import {
    MakeConclusionOrRateTrendComponent
} from './pages/analytics/trends/create-edit/make-conclusion-or-rate-trend/make-conclusion-or-rate-trend.component';
import {TrendDetailsComponent} from './pages/analytics/trends/details/trend-details.component';
import {TrendsComponent} from './pages/analytics/trends/trends.component';
import {ErrorComponent} from './pages/error/error.component';
import {CreateEditIncidentComponent} from './pages/incidents/create-edit/create-edit-incident.component';
import {ContactCreateEditComponent} from './pages/incidents/details/contacts-tab/contact-create-edit/contact-create-edit.component';
import {ContactDetailsPageComponent} from './pages/incidents/details/contacts-tab/contact-details/contact-details.component';
import {MsisdnFraudEntryCreateEditComponent} from './pages/incidents/details/msisdn-fraud-entries-tab/msisdn-fraud-entry-create-edit/msisdn-fraud-entry-create-edit.component';
import {IncidentDetailsComponent} from './pages/incidents/details/incident-details.component';
import {ResolutionCreateComponent} from './pages/incidents/details/resolution-create/resolution-create.component';
import {
    TransactionCreateEditComponent,
} from './pages/incidents/details/transactions-tab/transaction-create-edit/transaction-create-edit.component';
import {TransactionDetailsComponent} from './pages/incidents/details/transactions-tab/transaction-details/transaction-details.component';
import {IncidentsComponent} from './pages/incidents/incidents.component';
import {CreateEditFaqComponent} from './pages/knowledge-base/faq-tab/create-edit-faq/create-edit-faq.component';
import {FaqDetailsComponent} from './pages/knowledge-base/faq-tab/faq-admin/faq-details/faq-details.component';
import {
    KnowledgeBaseDocumentViewComponent,
} from './pages/knowledge-base/knowledge-base-document-view/knowledge-base-document-view.component';
import {
    KnowledgeBaseArchiveComponent,
} from './pages/knowledge-base/knowledge-base-documents-tab/documents-admin/archive/knowledge-base-archive.component';
import {
    CreateEditKnowledgeBaseDocumentComponent,
} from './pages/knowledge-base/knowledge-base-documents-tab/documents-admin/create-edit/create-edit-knowledge-base-document.component';
import {KnowledgeBaseComponent} from './pages/knowledge-base/knowledge-base.component';
import {LoginComponent} from './pages/login/login.component';
import {PasswordRecoveryComponent} from './pages/login/recovery/password-recovery.component';
import {NotificationsComponent} from './pages/notifications/notifications.component';
import {OauthCallbackComponent} from './pages/oauth-callback/oauth-callback.component';
import {RefundDetailsComponent} from './pages/refunds/refund-details/refund-details.component';
import {RefundsCreateEditComponent} from './pages/refunds/refunds-create-edit/refunds-create-edit.component';
import {RefundsComponent} from './pages/refunds/refunds.component';
import {ResetPasswordComponent} from './pages/reset-password/reset-password.component';
import {CreateEditSubjectComponent} from './pages/subjects/create-edit/create-edit-subject.component';
import {SubjectDetailsComponent} from './pages/subjects/details/subject-details.component';
import {MsisdnDetailsComponent} from './pages/subjects/msisdn-details/msisdn-details.component';
import {SubjectsComponent} from './pages/subjects/subjects.component';
import {Path} from './path';

export const appRoutes: Routes = [
    {path: Path.ROOT, pathMatch: 'full', redirectTo: Path.LOGIN},
    {path: Path.LOGIN, component: LoginComponent},
    {path: Path.PASSWORD_RECOVERY, component: PasswordRecoveryComponent},
    {path: Path.RESET_PASSWORD, component: ResetPasswordComponent},
    {path: VERSION_PATH, component: AppVersionComponent},
    {path: Path.OAUTH_CALLBACK, component: OauthCallbackComponent},
    {
        path: Path.ROOT,
        component: MainLayoutComponent,
        canActivate: [authGuard],
        children: [
            {path: ERROR_PATH, component: ErrorComponent},
            {path: Path.ANALYTICS, component: AnalyticsComponent, canActivate: [analyticsGuard]},
            {path: Path.ANALYTICS_INCIDENTS_DASHBOARDS, component: IncidentsFraudDashboardsComponent, canActivate: [analyticsGuard]},
            {
                path: Path.ANALYTICS_INCIDENTS_DASHBOARD_CREATE,
                component: DashboardFormComponent,
                canActivate: [analyticsGuard],
            },
            {
                path: Path.ANALYTICS_INCIDENTS_DASHBOARD_EDIT,
                component: DashboardFormComponent,
                canActivate: [analyticsGuard],
            },
            {path: Path.ANALYTICS_CLASSIFICATIONS_FRAUD, component: ClassificationFraudDashboardsComponent, canActivate: [analyticsGuard]},
            {path: Path.ANALYTICS_CLASSIFICATIONS_FRAUD_DETAILS, component: ClassificationDetailsComponent, canActivate: [analyticsGuard]},
            {
                path: Path.ANALYTICS_CLASSIFICATIONS_FRAUD_DETAILS_EDIT,
                component: ClassificationCreateEditCommonInformationComponent,
                canActivate: [analyticsGuard],
            },
            {path: Path.ANALYTICS_FRAUD_REPORTS, component: FraudReportsComponent, canActivate: [analyticsGuard]},
            {path: Path.ANALYTICS_FRAUD_REPORT_DETAILS, component: FraudReportDetailsComponent, canActivate: [analyticsGuard]},
            {path: Path.ANALYTICS_TRENDS, component: TrendsComponent, canActivate: [analyticsGuard]},
            {path: Path.ANALYTICS_TREND_DETAILS, component: TrendDetailsComponent, canActivate: [analyticsGuard]},
            {
                path: Path.ANALYTICS_TREND_DETAILS_CONCLUSION,
                component: MakeConclusionOrRateTrendComponent,
                canActivate: [analyticsGuard],
            },
            {
                path: Path.ANALYTICS_TREND_DETAILS_RATE,
                component: MakeConclusionOrRateTrendComponent,
                canActivate: [analyticsGuard],
            },
            {
                path: Path.ANALYTICS_TREND_EDIT,
                component: CreateEditTrendComponent,
                canActivate: [analyticsGuard],
            },
            {path: Path.ADMIN_PANEL, component: AdminComponent, canActivate: [adminPanelUsersGuard]},
            {path: Path.ADMIN_PANEL_USERS_CREATE, component: UserCreateEditComponent, canActivate: [adminPanelUsersGuard]},
            {path: Path.ADMIN_PANEL_USERS + `/:${USER_ID}`, component: UserDetailsComponent, canActivate: [adminPanelUsersGuard]},
            {path: Path.ADMIN_PANEL_USERS_EDIT, component: UserCreateEditComponent, canActivate: [adminPanelUsersGuard]},
            {path: Path.ADMIN_PANEL_ORGANIZATIONS_CREATE, component: OrganizationCreateEditComponent, canActivate: [adminPanelGuard]},
            {path: Path.ADMIN_PANEL_ORGANIZATION_EDIT, component: OrganizationCreateEditComponent, canActivate: [adminPanelGuard]},
            {path: Path.ADMIN_PANEL_ORGANIZATION_USER_CREATE, component: UserCreateEditComponent, canActivate: [adminPanelGuard]},
            {path: Path.ADMIN_PANEL_ORGANIZATION_USER_DETAILS, component: UserDetailsComponent, canActivate: [adminPanelGuard]},
            {
                path: Path.ADMIN_PANEL_ORGANIZATION_DOCUMENT_CREATE,
                component: OrganizationDocumentCreateEditComponent,
                canActivate: [adminPanelGuard],
            },
            {
                path: Path.ADMIN_PANEL_ORGANIZATION_DOCUMENT_EDIT,
                component: OrganizationDocumentCreateEditComponent,
                canActivate: [adminPanelGuard],
            },
            {path: Path.ADMIN_PANEL_ORGANIZATION_DETAILS, component: OrganizationDetailsComponent, canActivate: [adminPanelGuard]},
            {path: Path.ADMIN_PANEL_DICTIONARY_ADD_VALUE, component: DictionaryCreateEditComponent, canActivate: [adminPanelGuard]},
            {path: Path.ADMIN_PANEL_DICTIONARY_EDIT_VALUE, component: DictionaryCreateEditComponent, canActivate: [adminPanelGuard]},
            {path: Path.INCIDENTS, component: IncidentsComponent},
            {path: Path.INCIDENTS_CREATE, component: CreateEditIncidentComponent},
            {path: Path.INCIDENTS_EDIT, component: CreateEditIncidentComponent},
            {path: Path.INCIDENTS_RESOLUTION, component: ResolutionCreateComponent},
            {path: Path.INCIDENTS_CREATE_REFUND, component: RefundsCreateEditComponent},
            {path: Path.INCIDENTS_SUBJECT_DETAILS, component: SubjectDetailsComponent},
            {path: Path.INCIDENTS_REFUND_DETAILS, component: RefundDetailsComponent},
            {path: Path.INCIDENTS_REFUND_EDIT, component: RefundsCreateEditComponent},
            {path: Path.INCIDENTS_CONTACT_DETAILS_CREATE, component: ContactCreateEditComponent},
            {path: Path.INCIDENTS_CONTACT_DETAILS_EDIT, component: ContactCreateEditComponent},
            {path: Path.INCIDENTS_CONTACT_DETAILS_DETAILS, component: ContactDetailsPageComponent},
            {path: Path.INCIDENTS_MSISDN_FRAUD_ENTRY_CREATE, component: MsisdnFraudEntryCreateEditComponent},
            {path: Path.INCIDENTS_MSISDN_FRAUD_ENTRY_EDIT, component: MsisdnFraudEntryCreateEditComponent},
            {path: Path.INCIDENTS_TRANSACTIONS_CREATE, component: TransactionCreateEditComponent},
            {path: Path.INCIDENTS_TRANSACTIONS_DETAILS, component: TransactionDetailsComponent},
            {path: Path.INCIDENTS_TRANSACTIONS_EDIT, component: TransactionCreateEditComponent},
            {path: Path.INCIDENT_DETAILS, component: IncidentDetailsComponent},
            {path: Path.REFUNDS, component: RefundsComponent, canActivate: [refundsGuard]},
            {path: Path.REFUND_EDIT, component: RefundsCreateEditComponent, canActivate: [refundsGuard]},
            {path: Path.REFUND_DETAILS, component: RefundDetailsComponent, canActivate: [refundsGuard]},
            {path: Path.REFUNDS_TRANSACTIONS_DETAILS, component: TransactionDetailsComponent, canActivate: [refundsGuard]},
            {path: Path.SUBJECTS, component: SubjectsComponent},
            {path: Path.SUBJECTS_CREATE, component: CreateEditSubjectComponent},
            {path: Path.SUBJECTS_EDIT, component: CreateEditSubjectComponent},
            {path: Path.SUBJECT_DETAILS, component: SubjectDetailsComponent},
            {path: Path.SUBJECTS_CONTACT_DETAILS_DETAILS, component: ContactDetailsPageComponent},
            {path: Path.NOTIFICATIONS, component: NotificationsComponent},
            {path: Path.AF_CENTRE, component: AfCentreComponent, canActivate: [afCentreGuard]},
            {path: Path.MSISDN_DETAILS, component: MsisdnDetailsComponent},
            {path: Path.FAQ_CREATE, component: CreateEditFaqComponent, canActivate: [npckAdminGuard]},
            {path: Path.FAQ_DETAILS, component: FaqDetailsComponent, canActivate: [npckAdminGuard]},
            {path: Path.FAQ_EDIT, component: CreateEditFaqComponent, canActivate: [npckAdminGuard]},
            {path: Path.KNOWLEDGE_BASE, component: KnowledgeBaseComponent},
            {path: Path.KNOWLEDGE_BASE_ARCHIVE, component: KnowledgeBaseArchiveComponent, canActivate: [npckAdminGuard]},
            {path: Path.KNOWLEDGE_BASE_DOC_CREATE, component: CreateEditKnowledgeBaseDocumentComponent, canActivate: [npckAdminGuard]},
            {path: Path.KNOWLEDGE_BASE_DOC_EDIT, component: CreateEditKnowledgeBaseDocumentComponent, canActivate: [npckAdminGuard]},
            {path: Path.KNOWLEDGE_BASE_DOC_VIEW, component: KnowledgeBaseDocumentViewComponent},
        ],
    },
    {path: ERROR_PATH, component: ErrorComponent},
    {path: '**', redirectTo: Path.ROOT},
];
